<template>
  <teleport v-if="renderMode === 'modal'" to="body">
    <div class="modal" :class="{ 'modal-open': visible === true }" v-show="visible === true">
      <div class="modal-box" :class="boxClass">
        <div v-if="$slots.header !== undefined || title !== '' || $slots.left !== undefined" class="app-modal-header-new" :class="{ 'app-modal-header-new-rtl': isRTL === true }">
          <div class="app-modal-header-top">
            <div class="app-modal-header-left">
              <slot name="left"></slot>
            </div>
            <div class="app-modal-header-title-wrapper" :dir="isRTL === true ? 'rtl' : 'ltr'">
              <slot name="header">
                <h3 class="app-modal-header-title" :class="{ 'app-modal-header-title-rtl': isRTL === true }">{{ title }}</h3>
              </slot>
            </div>
            <div class="app-modal-header-right">
              <button v-if="showCloseButton === true" class="btn btn-sm btn-circle btn-ghost app-modal-close" @click="emitClose">✕</button>
            </div>
          </div>
        </div>
        <div class="app-modal-content">
          <slot></slot>
        </div>
        <div v-if="$slots.actions" class="modal-action">
          <slot name="actions"></slot>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop"><button @click="emitClose">close</button></form>
    </div>
  </teleport>

  <div v-else-if="visible === true" class="app-modal-panel-shell">
    <div v-if="panelPlain === true" class="app-modal-panel-plain-shell" :class="boxClass">
      <slot></slot>
      <div v-if="$slots.actions" class="modal-action app-modal-panel-plain-actions">
        <slot name="actions"></slot>
      </div>
    </div>
    <div v-else class="app-modal-panel" :class="boxClass">
      <div v-if="$slots.header !== undefined || title !== '' || $slots.left !== undefined" class="app-modal-header-new" :class="{ 'app-modal-header-new-rtl': isRTL === true }">
        <div class="app-modal-header-top">
          <div class="app-modal-header-left">
            <slot name="left"></slot>
          </div>
          <div class="app-modal-header-title-wrapper" :dir="isRTL === true ? 'rtl' : 'ltr'">
            <slot name="header">
              <h3 class="app-modal-header-title" :class="{ 'app-modal-header-title-rtl': isRTL === true }">{{ title }}</h3>
            </slot>
          </div>
          <div class="app-modal-header-right">
            <button v-if="showCloseButton === true" class="btn btn-sm btn-circle btn-ghost app-modal-close" @click="emitClose">✕</button>
          </div>
        </div>
      </div>
      <slot></slot>
      <div v-if="$slots.actions" class="modal-action app-modal-panel-actions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { watch, onUnmounted, computed, ref } from 'vue';
import { useI18n } from '@/i18n';

let openAppModalCount = 0;

const props = defineProps({
  visible: { type: Boolean, default: false },
  title: { type: String, default: '' },
  boxClass: { type: String, default: '' },
  showCloseButton: { type: Boolean, default: true },
  renderMode: { type: String, default: 'modal' },
  panelPlain: { type: Boolean, default: false }
});
const emit = defineEmits(['close']);
const holdsScrollLock = ref(false);

const { currentLanguage } = useI18n();
const isRTL = computed(() => {
  const lang = (currentLanguage?.value !== undefined ? currentLanguage.value : '').toString().toLowerCase();
  return lang === 'he' || lang === 'ar' || (typeof lang === 'string' && (lang.startsWith('he') === true || lang.startsWith('ar') === true));
});

function emitClose() {
  emit('close');
}

function setScrollLock(open) {
  const shouldLock = open === true && props.renderMode === 'modal';
  if (shouldLock === holdsScrollLock.value) {
    return;
  }
  holdsScrollLock.value = shouldLock;
  if (shouldLock === true) {
    openAppModalCount = openAppModalCount + 1;
  } else if (openAppModalCount > 0) {
    openAppModalCount = openAppModalCount - 1;
  }
  const html = document.documentElement;
  const body = document.body;
  const bodyScroll = document.getElementById('body-scroll');
  if (openAppModalCount > 0) {
    if (html !== null) html.classList.add('app-modal-open');
    if (body !== null) body.classList.add('app-modal-open');
    if (bodyScroll !== null) bodyScroll.classList.add('modal-page-scroll-locked');
  } else {
    if (html !== null) html.classList.remove('app-modal-open');
    if (body !== null) body.classList.remove('app-modal-open');
    if (bodyScroll !== null) bodyScroll.classList.remove('modal-page-scroll-locked');
  }
}

watch(() => props.visible, (isOpen) => {
  setScrollLock(isOpen);
}, { immediate: true });

onUnmounted(() => {
  setScrollLock(false);
});
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-box {
  display: flex;
  flex-direction: column;
  max-height: calc(100dvh - 2rem);
  overflow: hidden;
  border-radius: 1.5rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: var(--fallback-b1, #ffffff);
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.28);
}
.app-modal-content {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.app-modal-header-new {
  margin-bottom: 0.75rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--fallback-b2, rgba(148, 163, 184, 0.3));
  overflow: hidden;
  flex-shrink: 0;
}
.app-modal-header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  direction: ltr;
  min-height: auto;
}
.app-modal-header-new-rtl .app-modal-header-top {
  direction: rtl;
  flex-direction: row-reverse;
}
.app-modal-header-left {
  flex-shrink: 0;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
}
.app-modal-header-new-rtl .app-modal-header-left {
  margin-right: 0;
  margin-left: 0.5rem;
}
.app-modal-header-title-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 0 0.25rem;
  overflow: hidden;
}
.app-modal-header-title {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.3;
  margin: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  direction: ltr;
  text-align: left;
}
.app-modal-header-title-rtl {
  direction: rtl;
  text-align: right;
}
.app-modal-header-right {
  flex-shrink: 0;
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
}
.app-modal-header-new-rtl .app-modal-header-right {
  margin-left: 0;
  margin-right: 0.5rem;
}
.app-modal-close {
  flex-shrink: 0;
}
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
}

.app-modal-panel-shell {
  display: flex;
  min-height: 0;
  height: 100%;
}

.app-modal-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: var(--fallback-b1, #ffffff);
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.08);
}

.app-modal-panel-plain-shell {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  width: 100%;
}

.app-modal-panel-actions {
  margin-top: auto;
  border-top: 1px solid var(--fallback-b2, rgba(148, 163, 184, 0.3));
  padding: 1rem;
}

.app-modal-panel-plain-actions {
  margin-top: auto;
  border-top: 1px solid var(--fallback-b2, rgba(148, 163, 184, 0.3));
  padding: 1rem 0 0;
}
</style>

<style>
html.app-modal-open, body.app-modal-open { overflow: hidden !important; }
</style>
